import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

const STORAGE_KEY = 'tp-theme';
const VALID = new Set(['light', 'dark', 'system']);

const ThemeContext = createContext({
  theme: 'system',
  resolved: 'light',
  setTheme: () => {},
});

function readStoredTheme() {
  try {
    const v = window.localStorage.getItem(STORAGE_KEY);
    return VALID.has(v) ? v : 'system';
  } catch {
    return 'system';
  }
}

function resolve(theme, systemPrefersDark) {
  if (theme === 'system') return systemPrefersDark ? 'dark' : 'light';
  return theme;
}

export function ThemeProvider({ children }) {
  const [theme, setThemeState] = useState(readStoredTheme);
  const [systemPrefersDark, setSystemPrefersDark] = useState(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return false;
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // Track system preference changes so theme='system' tracks the OS.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mql = window.matchMedia('(prefers-color-scheme: dark)');
    const handler = (e) => setSystemPrefersDark(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const resolved = resolve(theme, systemPrefersDark);

  // Reflect resolved theme onto <html data-bs-theme="..."> so Bootstrap 5.3's
  // native dark mode + our tokens.css take effect.
  useEffect(() => {
    if (typeof document === 'undefined') return;
    document.documentElement.dataset.bsTheme = resolved;
  }, [resolved]);

  const setTheme = useCallback((next) => {
    const v = VALID.has(next) ? next : 'system';
    setThemeState(v);
    try {
      window.localStorage.setItem(STORAGE_KEY, v);
    } catch {
      // ignore (private browsing, etc.)
    }
  }, []);

  const value = useMemo(() => ({ theme, resolved, setTheme }), [theme, resolved, setTheme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  return useContext(ThemeContext);
}

export { ThemeContext };
